import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  ChevronLeft,
  Check,
  X,
  ShoppingCart,
  Trash2,
  Users,
  Coffee,
  Utensils,
  Wifi,
  ChevronDown,
  ChevronRight,
  MapPin,
  Moon,
  Sun,
  Calendar,
  Heart,
  Search,
  Loader,
  UserPlus,
  Bed,
} from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";

const RoomBooking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get URL parameters
  const checkInDate = searchParams.get("checkIn") || "";
  const checkOutDate = searchParams.get("checkOut") || "";
  const guestsCount = searchParams.get("guests") || "1";

  // State for booking form
  const [checkIn, setCheckIn] = useState(checkInDate);
  const [checkOut, setCheckOut] = useState(checkOutDate);
  const [guests, setGuests] = useState(guestsCount);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [sortOption, setSortOption] = useState("recommended");
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showPriceOptions, setShowPriceOptions] = useState({});
  const [roomSelections, setRoomSelections] = useState({});
  const detailsRef = useRef(null);
  const [carouselIndices, setCarouselIndices] = useState({});

  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndices((prevIndices) => {
        const newIndices = { ...prevIndices };
        rooms.forEach((room) => {
          if (room.images && room.images.length > 0) {
            const currentIndex = prevIndices[room._id] || 0;
            newIndices[room._id] = (currentIndex + 1) % room.images.length;
          }
        });
        return newIndices;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [rooms]);

  useEffect(() => {
    const typeFromUrl = searchParams.get("type");
    if (typeFromUrl) {
      setActiveFilter(typeFromUrl.toLowerCase());
      navigate("/room-booking", { replace: true });
    }
  }, [searchParams, navigate]);

  // Fetch rooms data from API
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
      easing: "ease-in-out",
    });

    const fetchRooms = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get("https://apigulmohar.dodunsoftsolutions.com/api/rooms", {
          params: {
            checkIn: checkIn,
            checkOut: checkOut,
            guests: guests,
          },
        });

        if (response.data && Array.isArray(response.data)) {
          setRooms(response.data);
        } else {
          setError("No rooms data received from server");
          setRooms([]);
        }
      } catch (error) {
        console.error("Error fetching rooms:", error);
        setError(`Failed to load rooms: ${error.message}`);
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };

    if (checkIn && checkOut) {
      fetchRooms();
    } else {
      setLoading(false);
    }
  }, [checkIn, checkOut, guests]);

  // Save selected rooms to state
  useEffect(() => {
    // Could save to localStorage if needed
  }, [selectedRooms]);

  // Toggle favorite room
  const toggleFavorite = (roomId) => {
    setFavorites((prev) =>
      prev.includes(roomId)
        ? prev.filter((id) => id !== roomId)
        : [...prev, roomId]
    );
  };

  // Handle next image in modal gallery
  const handleNextImage = () => {
    if (selectedRoom && selectedRoom.images) {
      setCurrentImageIndex((prev) =>
        prev === selectedRoom.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  // Handle previous image in modal gallery
  const handlePrevImage = () => {
    if (selectedRoom && selectedRoom.images) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedRoom.images.length - 1 : prev - 1
      );
    }
  };

  // Enhanced filter with price range and sorting
  const filteredRooms = rooms
    .filter((room) => {
      // Room type filter
      const typeFilter =
        activeFilter === "all"
          ? true
          : activeFilter === "deluxe"
          ? room.roomType?.toLowerCase().includes("deluxe")
          : activeFilter === "executive"
          ? room.roomType?.toLowerCase().includes("executive")
          : activeFilter === "suite"
          ? room.roomType?.toLowerCase().includes("suite")
          : activeFilter === "premium"
          ? room.roomType?.toLowerCase().includes("premium")
          : true;

      // Price range filter
      const priceFilter =
        room.price?.single >= priceRange[0] && room.price?.single <= priceRange[1];

      // Occupancy filter
      const occupancyFilter = room.occupancy >= parseInt(guests);

      // Status filter - only show available rooms
      const statusFilter = room.status === "available";

      return typeFilter && priceFilter && occupancyFilter && statusFilter;
    })
    .sort((a, b) => {
      // Sorting options
      if (sortOption === "price-low") return (a.price?.single || 0) - (b.price?.single || 0);
      if (sortOption === "price-high") return (b.price?.single || 0) - (a.price?.single || 0);
      if (sortOption === "occupancy") return (b.occupancy || 0) - (a.occupancy || 0);
      return 0;
    });

  // Update search params and trigger new search
  const handleSearch = () => {
    if (!checkIn || !checkOut) {
      alert("Please select both check-in and check-out dates");
      return;
    }

    const startDate = new Date(checkIn);
    const endDate = new Date(checkOut);
    if (endDate <= startDate) {
      alert("Check-out date must be after check-in date");
      return;
    }

    navigate(
      `/room-booking?checkIn=${checkIn}&checkOut=${checkOut}&guests=${guests}`,
      { replace: true }
    );

    setLoading(true);
  };

  // Handle room selection changes
  const handleRoomSelectionChange = (roomId, priceType, withBreakfast, extraPersons) => {
  const key = `${roomId}-${priceType}-${withBreakfast}-${extraPersons}`;
  
  setRoomSelections((prev) => ({
    ...prev,
    [roomId]: key,  // only one option per room
  }));

  const room = rooms.find(r => r._id === roomId);
  if (room) {
    toggleRoomSelection(room, priceType, withBreakfast, extraPersons);
  }
};

  // Calculate price with extra persons
  const calculatePriceWithExtras = (room, priceType, withBreakfast, extraPersons) => {
    let basePrice = withBreakfast 
      ? (priceType === 'single' ? room.price?.singleWithBreakfast : room.price?.doubleWithBreakfast)
      : (priceType === 'single' ? room.price?.single : room.price?.double);

    if (extraPersons > 0) {
      const extraPersonPrice = withBreakfast 
        ? room.price?.extraPersonWithBreakfast 
        : room.price?.extraPerson;
      basePrice = (basePrice || 0) + ((extraPersonPrice || 0) * extraPersons);
    }

    return basePrice;
  };

  // Toggle price options display
  const togglePriceOptions = (roomId) => {
    setShowPriceOptions(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  // Add or remove room from selection with specific price option
  const toggleRoomSelection = (room, priceType = 'single', withBreakfast = false, extraPersons = 0) => {
    if (!checkIn || !checkOut) {
      alert("Please select both check-in and check-out dates.");
      return;
    }

    const startDate = new Date(checkIn);
    const endDate = new Date(checkOut);
    if (endDate <= startDate) {
      alert("Check-out date must be after check-in date.");
      return;
    }

    // Calculate base price
    let selectedPrice = withBreakfast 
      ? (priceType === 'single' ? room.price?.singleWithBreakfast : room.price?.doubleWithBreakfast)
      : (priceType === 'single' ? room.price?.single : room.price?.double);

    // Add extra person charges
    if (extraPersons > 0) {
      const extraPersonPrice = withBreakfast 
        ? room.price?.extraPersonWithBreakfast 
        : room.price?.extraPerson;
      selectedPrice = (selectedPrice || 0) + ((extraPersonPrice || 0) * extraPersons);
    }

    setSelectedRooms((prev) => {
      const roomKey = `${room._id}-${priceType}-${withBreakfast}-${extraPersons}`;
      const isRoomSelected = prev.some(selectedRoom => 
        `${selectedRoom._id}-${selectedRoom.selectedPriceType}-${selectedRoom.withBreakfast}-${selectedRoom.extraPersons}` === roomKey
      );

      if (isRoomSelected) {
        return prev.filter(selectedRoom => 
          `${selectedRoom._id}-${selectedRoom.selectedPriceType}-${selectedRoom.withBreakfast}-${selectedRoom.extraPersons}` !== roomKey
        );
      } else {
        return [
          ...prev.filter(r => r._id !== room._id),
          {
            ...room,
            checkIn,
            checkOut,
            guests,
            selectedPrice,
            selectedPriceType: priceType,
            withBreakfast,
            extraPersons,
            priceLabel: `${priceType === 'single' ? 'Single' : 'Double'} ${withBreakfast ? 'with Breakfast' : ''}${extraPersons > 0 ? ` + ${extraPersons} Extra` : ''}`
          },
        ];
      }
    });
  };

  // Remove room from selection
  const removeRoomFromSelection = (roomId) => {
  setSelectedRooms((prev) => prev.filter(room => room._id !== roomId));
  setRoomSelections((prev) => {
    const updated = { ...prev };
    delete updated[roomId];
    return updated;
  });
};

  // Check if a specific room configuration is selected
  const isRoomSelected = (roomId, priceType = 'single', withBreakfast = false, extraPersons = 0) => {
    const roomKey = `${roomId}-${priceType}-${withBreakfast}-${extraPersons}`;
    return selectedRooms.some(room => 
      `${room._id}-${room.selectedPriceType}-${room.withBreakfast}-${room.extraPersons}` === roomKey
    );
  };

  // Proceed to checkout
  const proceedToCheckout = () => {
  if (selectedRooms.length === 0) {
    alert("Please select at least one room to continue");
    return;
  }

  // Extract required data
  const selectedData = selectedRooms.map(room => ({
    roomId: room._id,
    priceType: room.selectedPriceType,
    withBreakfast: room.withBreakfast,
    extraPersons: room.extraPersons
  }));

  const encodedData = encodeURIComponent(JSON.stringify(selectedData));

  const params = new URLSearchParams({
    guestsCount,
    rooms: encodedData,
    checkIn,
    checkOut,
  }).toString();

  navigate(`/booking/checkout?${params}`);
};

  // Calculate number of nights
  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays || 1;
  };

  const nights = calculateNights();

  // Calculate total price for selected rooms
  const calculateTotalPrice = () => {
    return selectedRooms.reduce(
      (total, room) => total + (room.selectedPrice || 0) * nights,
      0
    );
  };

  // Format price display
  const formatPrice = (price) => {
    if (!price && price !== 0) return "Price not available";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with booking form */}
      <div className="hidden md:block bg-black text-white sticky top-0 z-30 shadow-xl">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-between mt-44">
            <motion.div
              className="absolute -bottom-6 left-0 right-0 hidden md:block z-30"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="container mx-auto font-serif">
                <div className="bg-black/50 backdrop-blur-lg border border-white/20 rounded-sm shadow-2xl p-6 max-w-4xl mx-auto flex flex-wrap justify-between items-center">
                  <div className="w-full md:w-1/4 p-2">
                    <label className="text-white/80 text-xs uppercase tracking-wider mb-1 block">
                      Check-In
                    </label>
                    <input
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full bg-transparent border-b border-white/30 text-white py-2 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div className="w-full md:w-1/4 p-2">
                    <label className="text-white/80 text-xs uppercase tracking-wider mb-1 block">
                      Check-Out
                    </label>
                    <input
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full bg-transparent border-b border-white/30 text-white py-2 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div className="w-full md:w-1/4 p-2">
                    <label className="text-white/80 text-xs uppercase tracking-wider mb-1 block">
                      Guests
                    </label>
                    <select
                      value={guests}
                      onChange={(e) => setGuests(e.target.value)}
                      className="w-full bg-transparent border-b border-white/30 text-white py-2 focus:outline-none focus:border-amber-500"
                    >
                      <option className="bg-gray-900" value="1">1 Guest</option>
                      <option className="bg-gray-900" value="2">2 Guests</option>
                      <option className="bg-gray-900" value="3">3 Guests</option>
                      <option className="bg-gray-900" value="4">4 Guests</option>
                    </select>
                  </div>
                  <motion.button
                    onClick={handleSearch}
                    className="bg-amber-700 hover:bg-amber-600 text-white px-6 py-2 rounded-sm uppercase tracking-wider text-sm transition-all transform hover:scale-105 ml-auto flex items-center gap-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Search className="w-4 h-4" />
                    Search
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Cart/Selection Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <motion.button
          onClick={() => setShowCart(!showCart)}
          className="bg-amber-700 hover:bg-amber-600 text-white p-4 rounded-full shadow-lg flex items-center gap-2 relative"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ShoppingCart className="w-6 h-6" />
          {selectedRooms.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
              {selectedRooms.length}
            </span>
          )}
        </motion.button>
      </div>

      {/* Selected Rooms Cart Modal */}
      <AnimatePresence>
        {showCart && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCart(false)}
          >
            <motion.div
              className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-serif text-gray-800">Selected Rooms</h2>
                  <button
                    onClick={() => setShowCart(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {selectedRooms.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-600 mb-2">
                      Your selection is empty
                    </h3>
                    <p className="text-gray-500 mb-6">
                      Add rooms to your selection to continue with booking
                    </p>
                    <button
                      onClick={() => setShowCart(false)}
                      className="bg-amber-700 hover:bg-amber-600 text-white px-6 py-2 rounded-md text-sm transition-all"
                    >
                      Browse Rooms
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6">
                      {selectedRooms.map((room, index) => (
                        <div key={`${room._id}-${room.selectedPriceType}-${room.withBreakfast}-${index}`} className="flex gap-4 border-b border-gray-100 pb-4">
                          <div className="w-24 h-24 flex-shrink-0">
                            <img
                              src={room.images && room.images[0] ? room.images[0] : "/api/placeholder/96/96"}
                              alt={room.roomType}
                              className="w-full h-full object-cover rounded-md"
                            />
                          </div>
                          <div className="flex-grow">
                            <div className="flex justify-between">
                              <h3 className="font-medium text-gray-800">{room.roomType}</h3>
                              <p className="font-medium text-amber-700">
                                {formatPrice(room.selectedPrice)}
                              </p>
                            </div>
                            <p className="text-sm text-gray-500">
                              Room {room.roomNumber}, Floor {room.floor}
                            </p>
                            <p className="text-sm text-gray-500">
                              {room.priceLabel}
                            </p>
                            <p className="text-sm text-gray-500">
                              {room.occupancy} {room.occupancy === 1 ? "Person" : "People"} · {room.bedType}
                            </p>
                          </div>
                          <button
                            onClick={() => removeRoomFromSelection(room._id)}
                            className="text-gray-400 hover:text-red-500 p-1 self-start"
                            aria-label="Remove room"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="bg-amber-50 p-4 rounded-md mb-6">
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-700">
                          Total for {nights} {nights === 1 ? "night" : "nights"}
                        </span>
                        <span className="font-medium text-gray-900">
                          {formatPrice(calculateTotalPrice())}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">Taxes and fees included</p>
                    </div>

                    <div className="flex justify-between">
                      <button
                        onClick={() => setShowCart(false)}
                        className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50"
                      >
                        Continue Browsing
                      </button>
                      <button
                        onClick={proceedToCheckout}
                        className="bg-amber-700 hover:bg-amber-600 text-white px-6 py-2 rounded-md font-medium transition-all"
                      >
                        Proceed to Checkout
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="container mx-auto px-4 py-8">
        {/* Booking summary */}
        {checkIn && checkOut && (
          <div className="bg-white p-6 rounded-md shadow-md mb-8 mt-24 md:mt-0" data-aos="fade-up" data-aos-delay="100">
            <h2 className="text-2xl font-serif text-gray-800 mb-4">Your Stay Details</h2>
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Check-In</p>
                <p className="text-lg text-gray-800">
                  {new Date(checkIn).toLocaleDateString("en-US", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Check-Out</p>
                <p className="text-lg text-gray-800">
                  {new Date(checkOut).toLocaleDateString("en-US", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Duration</p>
                <p className="text-lg text-gray-800">
                  {nights} {nights === 1 ? "Night" : "Nights"}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">Guests</p>
                <p className="text-lg text-gray-800">
                  {guests} {parseInt(guests) === 1 ? "Person" : "People"}
                </p>
              </div>

              {selectedRooms.length > 0 && (
                <div className="ml-auto">
                  <p className="text-xs uppercase tracking-wider text-gray-500">Selected Rooms</p>
                  <div className="flex items-center">
                    <p className="text-lg text-gray-800 mr-2">{selectedRooms.length}</p>
                    <button
                      onClick={() => setShowCart(true)}
                      className="text-amber-700 hover:underline text-sm flex items-center"
                    >
                      View Selection <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-8">
            <div className="flex">
              <X className="w-5 h-5 text-red-400 mr-2 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error loading rooms</h3>
                <div className="mt-1 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Room filter */}
        <div className="mb-8 flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="200">
          <button
            onClick={() => setActiveFilter("all")}
            className={`px-6 py-2 rounded-full transition-all ${
              activeFilter === "all"
                ? "bg-amber-700 text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            All Rooms
          </button>
          <button
            onClick={() => setActiveFilter("deluxe")}
            className={`px-6 py-2 rounded-full transition-all ${
              activeFilter === "deluxe"
                ? "bg-amber-700 text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            Deluxe Rooms
          </button>
          <button
            onClick={() => setActiveFilter("executive")}
            className={`px-6 py-2 rounded-full transition-all ${
              activeFilter === "executive"
                ? "bg-amber-700 text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            Executive Rooms
          </button>
          <button
            onClick={() => setActiveFilter("suite")}
            className={`px-6 py-2 rounded-full transition-all ${
              activeFilter === "suite"
                ? "bg-amber-700 text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            Suites
          </button>
          <button
            onClick={() => setActiveFilter("premium")}
            className={`px-6 py-2 rounded-full transition-all ${
              activeFilter === "premium"
                ? "bg-amber-700 text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            Premium Rooms
          </button>
        </div>

        {/* Room listings */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-700"></div>
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="bg-white p-10 rounded-md shadow-md text-center">
            <h3 className="text-xl font-medium mb-2">No rooms available</h3>
            <p className="text-gray-600 mb-4">
              {!checkIn || !checkOut 
                ? "Please select check-in and check-out dates to view available rooms."
                : "Please try adjusting your search criteria or dates."
              }
            </p>
            <button
              onClick={() => setActiveFilter("all")}
              className="bg-amber-700 hover:bg-amber-600 text-white px-6 py-2 uppercase tracking-wider text-sm"
            >
              View All Rooms
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredRooms.map((room, index) => (
              <motion.div
                key={room._id}
                className="bg-white rounded-lg shadow-md overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                data-aos="fade-up"
                data-aos-delay={200 + index * 100}
              >
                <div className="md:flex">
                  {/* Room image */}
                  <div className="md:w-1/3 relative overflow-hidden h-64 md:h-auto">
                    {room.images && room.images.length > 0 ? (
                      room.images.map((img, imgIndex) => (
                        <img
                          key={imgIndex}
                          src={img}
                          alt={`${room.roomType} image ${imgIndex + 1}`}
                          className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out ${
                            (carouselIndices[room._id] || 0) === imgIndex
                              ? "opacity-100 z-10"
                              : "opacity-0 z-0"
                          }`}
                        />
                      ))
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400">No image available</span>
                      </div>
                    )}
                    <div className="absolute top-4 left-4 bg-amber-700 text-white px-4 py-1 text-sm uppercase tracking-wider z-20">
                      {room.roomType}
                    </div>

                    <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center z-20">
                      <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-sm shadow-md">
                        <p className="text-amber-800 text-xs font-medium">
                          Room {room.roomNumber}, Floor {room.floor}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Room details */}
                  <div className="p-6 md:w-2/3 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-serif text-gray-800">{room.roomType}</h3>
                        <div className="flex items-center space-x-1 mt-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-amber-500" fill="currentColor" />
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <button
                          onClick={() => togglePriceOptions(room._id)}
                          className="flex items-center gap-2 text-amber-700 hover:text-amber-800 transition-colors"
                        >
                          <div className="text-right">
                            <p className="text-lg font-serif text-amber-700">
                              From {formatPrice(room.price?.single)}
                            </p>
                            <p className="text-sm text-gray-500">per night</p>
                          </div>
                          <ChevronDown className={`w-4 h-4 transition-transform ${showPriceOptions[room._id] ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </div>

                    {/* Expandable pricing options */}
                    <AnimatePresence>
                      {showPriceOptions[room._id] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mb-4 bg-amber-50 rounded-lg p-4 overflow-hidden"
                        >
                          <h4 className="font-medium text-gray-800 mb-3">Pricing Options</h4>
                          <div className="space-y-4">
                            {/* Single Occupancy Options */}
                            <div className="bg-white rounded-md p-4 border border-amber-200">
                              <h5 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                                <Users className="w-4 h-4 text-amber-600" />
                                Single Occupancy
                              </h5>
                              <div className="space-y-3">
                                {/* Single without breakfast */}
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      id={`${room._id}-single-false-0`}
                                      checked={roomSelections[room._id] === `${room._id}-single-false-0`}
                                      onChange={(e) => handleRoomSelectionChange(room._id, 'single', false, 0, e.target.checked)}
                                      className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                    />
                                    <label htmlFor={`${room._id}-single-false-0`} className="text-sm font-medium text-gray-700">
                                      Standard Rate
                                    </label>
                                  </div>
                                  <span className="text-amber-700 font-semibold">
                                    {formatPrice(room.price?.single)}
                                  </span>
                                </div>
                                
                                {/* Single with breakfast */}
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      id={`${room._id}-single-true-0`}
                                      checked={roomSelections[room._id] === `${room._id}-single-true-0`}
                                      onChange={(e) => handleRoomSelectionChange(room._id, 'single', true, 0, e.target.checked)}
                                      className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                    />
                                    <label htmlFor={`${room._id}-single-true-0`} className="text-sm font-medium text-gray-700 flex items-center gap-1">
                                      <Coffee className="w-3 h-3" />
                                      With Breakfast
                                    </label>
                                  </div>
                                  <span className="text-amber-700 font-semibold">
                                    {formatPrice(room.price?.singleWithBreakfast)}
                                  </span>
                                </div>

                                {/* Single with extra persons */}
                                {(room.price?.extraPerson || room.price?.extraPersonWithBreakfast) && (
                                  <div className="border-t pt-2">
                                    <p className="text-xs text-gray-600 mb-2">With Extra Person:</p>
                                    {[1, 2].map(extraCount => (
                                      <div key={extraCount} className="space-y-2 mb-2">
                                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                          <div className="flex items-center gap-3">
                                            <input
                                              type="checkbox"
                                              id={`${room._id}-single-false-${extraCount}`}
                                              checked={roomSelections[room._id] === `${room._id}-single-false-${extraCount}`}
                                              onChange={(e) => handleRoomSelectionChange(room._id, 'single', false, extraCount, e.target.checked)}
                                              className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                            />
                                            <label htmlFor={`${room._id}-single-false-${extraCount}`} className="text-xs text-gray-600 flex items-center gap-1">
                                              <UserPlus className="w-3 h-3" />
                                              +{extraCount} Person{extraCount > 1 ? 's' : ''}
                                            </label>
                                          </div>
                                          <span className="text-amber-700 font-medium text-sm">
                                            {formatPrice(calculatePriceWithExtras(room, 'single', false, extraCount))}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                          <div className="flex items-center gap-3">
                                            <input
                                              type="checkbox"
                                              id={`${room._id}-single-true-${extraCount}`}
                                              checked={roomSelections[room._id] === `${room._id}-single-true-${extraCount}`}
                                              onChange={(e) => handleRoomSelectionChange(room._id, 'single', true, extraCount, e.target.checked)}
                                              className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                            />
                                            <label htmlFor={`${room._id}-single-true-${extraCount}`} className="text-xs text-gray-600 flex items-center gap-1">
                                              <UserPlus className="w-3 h-3" />
                                              <Coffee className="w-3 h-3" />
                                              +{extraCount} Person{extraCount > 1 ? 's' : ''} + Breakfast
                                            </label>
                                          </div>
                                          <span className="text-amber-700 font-medium text-sm">
                                            {formatPrice(calculatePriceWithExtras(room, 'single', true, extraCount))}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Double Occupancy Options */}
                            <div className="bg-white rounded-md p-4 border border-amber-200">
                              <h5 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                                <Users className="w-4 h-4 text-amber-600" />
                                Double Occupancy
                              </h5>
                              <div className="space-y-3">
                                {/* Double without breakfast */}
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      id={`${room._id}-double-false-0`}
                                      checked={roomSelections[room._id] === `${room._id}-double-false-0`}
                                      onChange={(e) => handleRoomSelectionChange(room._id, 'double', false, 0, e.target.checked)}
                                      className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                    />
                                    <label htmlFor={`${room._id}-double-false-0`} className="text-sm font-medium text-gray-700">
                                      Standard Rate
                                    </label>
                                  </div>
                                  <span className="text-amber-700 font-semibold">
                                    {formatPrice(room.price?.double)}
                                  </span>
                                </div>
                                
                                {/* Double with breakfast */}
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      id={`${room._id}-double-true-0`}
                                      checked={roomSelections[room._id] === `${room._id}-double-true-0`}
                                      onChange={(e) => handleRoomSelectionChange(room._id, 'double', true, 0, e.target.checked)}
                                      className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                    />
                                    <label htmlFor={`${room._id}-double-true-0`} className="text-sm font-medium text-gray-700 flex items-center gap-1">
                                      <Coffee className="w-3 h-3" />
                                      With Breakfast
                                    </label>
                                  </div>
                                  <span className="text-amber-700 font-semibold">
                                    {formatPrice(room.price?.doubleWithBreakfast)}
                                  </span>
                                </div>

                                {/* Double with extra persons */}
                                {(room.price?.extraPerson || room.price?.extraPersonWithBreakfast) && (
                                  <div className="border-t pt-2">
                                    <p className="text-xs text-gray-600 mb-2">With Extra Person:</p>
                                    {[1, 2].map(extraCount => (
                                      <div key={extraCount} className="space-y-2 mb-2">
                                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                          <div className="flex items-center gap-3">
                                            <input
                                              type="checkbox"
                                              id={`${room._id}-double-false-${extraCount}`}
                                              checked={roomSelections[room._id] === `${room._id}-double-false-${extraCount}`}
                                              onChange={(e) => handleRoomSelectionChange(room._id, 'double', false, extraCount, e.target.checked)}
                                              className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                            />
                                            <label htmlFor={`${room._id}-double-false-${extraCount}`} className="text-xs text-gray-600 flex items-center gap-1">
                                              <UserPlus className="w-3 h-3" />
                                              +{extraCount} Person{extraCount > 1 ? 's' : ''}
                                            </label>
                                          </div>
                                          <span className="text-amber-700 font-medium text-sm">
                                            {formatPrice(calculatePriceWithExtras(room, 'double', false, extraCount))}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                          <div className="flex items-center gap-3">
                                            <input
                                              type="checkbox"
                                              id={`${room._id}-double-true-${extraCount}`}
                                              checked={roomSelections[room._id] === `${room._id}-double-true-${extraCount}`}
                                              onChange={(e) => handleRoomSelectionChange(room._id, 'double', true, extraCount, e.target.checked)}
                                              className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                            />
                                            <label htmlFor={`${room._id}-double-true-${extraCount}`} className="text-xs text-gray-600 flex items-center gap-1">
                                              <UserPlus className="w-3 h-3" />
                                              <Coffee className="w-3 h-3" />
                                              +{extraCount} Person{extraCount > 1 ? 's' : ''} + Breakfast
                                            </label>
                                          </div>
                                          <span className="text-amber-700 font-medium text-sm">
                                            {formatPrice(calculatePriceWithExtras(room, 'double', true, extraCount))}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <p className="text-gray-600 mb-4 line-clamp-2">{room.description}</p>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                      <div className="flex items-center text-gray-700">
                        <Users className="w-4 h-4 mr-2 text-amber-700" />
                        <span className="text-sm">Sleeps {room.occupancy}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Bed className="w-4 h-4 mr-2 text-amber-700" />
                        <span className="text-sm">{room.bedType}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        {room.meals?.breakfast ? (
                          <>
                            <Coffee className="w-4 h-4 mr-2 text-amber-700" />
                            <span className="text-sm">Breakfast Available</span>
                          </>
                        ) : (
                          <>
                            <X className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-400">No Breakfast</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Wifi className="w-4 h-4 mr-2 text-amber-700" />
                        <span className="text-sm">Free WiFi</span>
                      </div>
                      {room.meals?.dinner && (
                        <div className="flex items-center text-gray-700">
                          <Utensils className="w-4 h-4 mr-2 text-amber-700" />
                          <span className="text-sm">Dinner Available</span>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-gray-100 pt-4 mt-auto flex flex-wrap justify-between items-center">
                      <div>
                        <button
                          onClick={() => {
                            setSelectedRoom(room);
                            setShowDetails(true);
                            setCurrentImageIndex(0);
                          }}
                          className="text-amber-700 flex items-center hover:underline"
                        >
                          View Details <ChevronRight className="w-4 h-4 ml-1" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          {selectedRooms.filter(r => r._id === room._id).length} selected
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Room details modal */}
      {showDetails && selectedRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            {/* Room images gallery */}
            <div className="relative h-80">
              {selectedRoom.images && selectedRoom.images.length > 0 ? (
                <>
                  <img
                    src={selectedRoom.images[currentImageIndex]}
                    alt={`${selectedRoom.roomType} - ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover transition-all duration-500"
                  />
                  
                  {/* Prev / Next buttons */}
                  {selectedRoom.images.length > 1 && (
                    <>
                      <button
                        onClick={handlePrevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/70 hover:bg-white text-black p-2 rounded-full shadow"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={handleNextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/70 hover:bg-white text-black p-2 rounded-full shadow"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </>
                  )}

                  {/* Dots for indicator */}
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                    {selectedRoom.images.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full ${
                          index === currentImageIndex ? "bg-white" : "bg-white/50"
                        }`}
                      ></div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400">No image available</span>
                </div>
              )}
              
              <button
                onClick={() => setShowDetails(false)}
                className="absolute top-4 right-4 bg-black/50 hover:bg-black text-white p-2 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-serif text-gray-800">{selectedRoom.roomType}</h2>
                  <p className="text-sm text-gray-500">
                    Room {selectedRoom.roomNumber}, Floor {selectedRoom.floor}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl text-amber-700 font-serif">
                    From {formatPrice(selectedRoom.price?.single)}
                  </p>
                  <p className="text-sm text-gray-500">per night</p>
                </div>
              </div>

              {/* Complete pricing breakdown in modal */}
              <div className="mb-6 bg-amber-50 rounded-lg p-4">
                <h3 className="text-lg font-medium mb-3">Complete Pricing</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-md p-3 border">
                    <h4 className="font-medium text-gray-800 mb-2">Standard Rates</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Single Occupancy:</span>
                        <span className="font-medium text-amber-700">
                          {formatPrice(selectedRoom.price?.single)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Double Occupancy:</span>
                        <span className="font-medium text-amber-700">
                          {formatPrice(selectedRoom.price?.double)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-md p-3 border">
                    <h4 className="font-medium text-gray-800 mb-2">With Breakfast</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Single + Breakfast:</span>
                        <span className="font-medium text-amber-700">
                          {formatPrice(selectedRoom.price?.singleWithBreakfast)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Double + Breakfast:</span>
                        <span className="font-medium text-amber-700">
                          {formatPrice(selectedRoom.price?.doubleWithBreakfast)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {(selectedRoom.price?.extraPerson || selectedRoom.price?.extraPersonWithBreakfast) && (
                    <div className="bg-white rounded-md p-3 border md:col-span-2">
                      <h4 className="font-medium text-gray-800 mb-2">Extra Person Charges</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {selectedRoom.price?.extraPerson && (
                          <div className="flex justify-between">
                            <span>Extra Person:</span>
                            <span className="font-medium text-amber-700">
                              {formatPrice(selectedRoom.price.extraPerson)}
                            </span>
                          </div>
                        )}
                        {selectedRoom.price?.extraPersonWithBreakfast && (
                          <div className="flex justify-between">
                            <span>Extra Person + Breakfast:</span>
                            <span className="font-medium text-amber-700">
                              {formatPrice(selectedRoom.price.extraPersonWithBreakfast)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Description</h3>
                <p className="text-gray-700">{selectedRoom.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Room Details</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700">
                      <Check className="w-4 h-4 mr-2 text-amber-700" />
                      <span>{selectedRoom.occupancy} {selectedRoom.occupancy === 1 ? "Person" : "People"} Occupancy</span>
                    </li>
                    <li className="flex items-center text-gray-700">
                      <Check className="w-4 h-4 mr-2 text-amber-700" />
                      <span>{selectedRoom.bedType}</span>
                    </li>
                    <li className="flex items-center text-gray-700">
                      <Check className="w-4 h-4 mr-2 text-amber-700" />
                      <span>Floor {selectedRoom.floor}</span>
                    </li>
                    <li className="flex items-center text-gray-700">
                      <Check className="w-4 h-4 mr-2 text-amber-700" />
                      <span>Private Bathroom</span>
                    </li>
                    <li className="flex items-center text-gray-700">
                      <Check className="w-4 h-4 mr-2 text-amber-700" />
                      <span>Air Conditioning</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-2">Amenities</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-700">
                      <Check className="w-4 h-4 mr-2 text-amber-700" />
                      <span>Free WiFi</span>
                    </li>
                    {selectedRoom.meals?.breakfast && (
                      <li className="flex items-center text-gray-700">
                        <Check className="w-4 h-4 mr-2 text-amber-700" />
                        <span>Breakfast Available</span>
                      </li>
                    )}
                    {selectedRoom.meals?.lunch && (
                      <li className="flex items-center text-gray-700">
                        <Check className="w-4 h-4 mr-2 text-amber-700" />
                        <span>Lunch Available</span>
                      </li>
                    )}
                    {selectedRoom.meals?.dinner && (
                      <li className="flex items-center text-gray-700">
                        <Check className="w-4 h-4 mr-2 text-amber-700" />
                        <span>Dinner Available</span>
                      </li>
                    )}
                    <li className="flex items-center text-gray-700">
                      <Check className="w-4 h-4 mr-2 text-amber-700" />
                      <span>Smart TV</span>
                    </li>
                  </ul>
                </div>
              </div>

              {selectedRoom.addons && selectedRoom.addons.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-2">Add-ons</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedRoom.addons.map((addon, index) => (
                      <div key={index} className="border border-gray-200 rounded-md p-3 flex justify-between items-center">
                        <span className="text-gray-700">{addon.name}</span>
                        <span className="text-amber-700 font-medium">{formatPrice(addon.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-4">
                <button
                  onClick={() => setShowDetails(false)}
                  className="border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-100 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default RoomBooking;